import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { orderBy } from "lodash";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { getDocs } from "firebase/firestore";

import {
  coownersCol,
  expensesCol,
  locationsCol,
  receiptsCol,
  suppliersCol,
} from "../../../firebase.config";

import {
  LocationInfo,
  ReceiptsFilter,
  ReceiptsTable,
  ExpensesTable,
  ExpensesFilter,
} from "./";

import { CoOwner, Expense, Receipt, Supplier } from "../../interfaces";

export const LocationData = () => {
  const [location, setLocation] = useState<any>();
  const [receipts, setReceipts] = useState<Receipt[]>();
  const [expenses, setExpenses] = useState<Expense[]>();
  const [coOwners, setCoOwners] = useState<CoOwner[]>();
  const [suppliers, setSuppliers] = useState<Supplier[]>();
  const [receiptFilterTerm, setReceiptFilterTerm] = useState("Svi");
  const [expensesFilterTerm, setExpensesFilterTerm] = useState("Svi");
  const [filteredReceipts, setFilteredReceipts] = useState<Receipt[]>();
  const [filteredExpenses, setFilteredExpenses] = useState<Expense[]>();

  let { locationId } = useParams();
  locationId = locationId?.toLowerCase();

  function orderDatesDesc<T>(data: T[]): T[] {
    return orderBy(
      data,
      [(item: any) => new Date(item.date.year, item.date.month, 1)],
      ["desc"]
    );
  }

  useEffect(() => {
    async function fetchLocation() {
      const data = await getDocs(locationsCol);

      const results = data.docs.map((doc) => ({
        ...doc.data(),
      }));

      const filteredResult = results.filter((item) => item.id === locationId);
      setLocation(filteredResult[0]);
    }

    fetchLocation();

    async function fetchReceipts() {
      const data = await getDocs(receiptsCol);

      const results = data.docs.map((doc) => ({
        ...doc.data(),
      }));

      const filteredResult = results.filter(
        (r: Receipt) => r.location === locationId
      );

      const sortedReceipts = orderDatesDesc(filteredResult);

      setReceipts(sortedReceipts);
      setFilteredReceipts(sortedReceipts);
    }

    fetchReceipts();

    async function fetchExpenses() {
      const data = await getDocs(expensesCol);

      const results = data.docs.map((doc) => ({
        ...doc.data(),
      }));

      const filteredResult = results.filter(
        (r: Expense) => r.location === locationId
      );

      const sortedExpenses = orderDatesDesc(filteredResult);

      setExpenses(sortedExpenses);
      setFilteredExpenses(sortedExpenses);
    }

    fetchExpenses();

    async function fetchCoOwners() {
      const data = await getDocs(coownersCol);

      const results = data.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));

      const filteredResult = results.filter(
        (r: CoOwner) => r.location === locationId
      );

      setCoOwners(filteredResult);
    }

    fetchCoOwners();

    async function fetchSuppliers() {
      const data = await getDocs(suppliersCol);

      const results = data.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));

      setSuppliers(results);
    }

    fetchSuppliers();
  }, [locationId]);

  useEffect(() => {
    if (receiptFilterTerm === "Svi") {
      setFilteredReceipts(receipts);
      return;
    }
    const result = receipts?.filter(
      (receipt: Receipt) => receipt.coowner === receiptFilterTerm
    );

    setFilteredReceipts(result);
  }, [receipts, receiptFilterTerm]);

  useEffect(() => {
    if (expensesFilterTerm === "Svi") {
      setFilteredExpenses(expenses);
      return;
    }
    const result = expenses?.filter(
      (expense: Expense) => expense.supplier === expensesFilterTerm
    );

    setFilteredExpenses(result);
  }, [expenses, expensesFilterTerm]);

  return (
    <Box
      sx={{
        padding: "3rem",
        "@media screen and (max-width: 768px)": {
          padding: "1rem",
          paddingBottom: "2rem",
        },
      }}
    >
      <Grid container gap={4} justifyContent="center">
        <Grid item xs={12} sx={{ padding: "1rem" }}>
          <LocationInfo location={location} />
        </Grid>
        <Card
          variant="elevation"
          sx={{ padding: "1rem", width: "100%", maxWidth: "1000px" }}
          raised
        >
          <Typography
            variant="h4"
            sx={{
              width: "100%",
              textAlign: "center",
              fontWeight: "600",
              marginBlock: "1rem 2rem",
            }}
          >
            UPLATE
          </Typography>
          <ReceiptsFilter
            receiptFilterTerm={receiptFilterTerm}
            setReceiptFilterTerm={setReceiptFilterTerm}
            coOwners={coOwners ?? []}
          />
          <Grid item xs={12} sx={{ marginTop: "2rem" }}>
            <ReceiptsTable
              coOwners={coOwners ?? []}
              receipts={filteredReceipts ?? []}
            />
          </Grid>
        </Card>
        <Card
          variant="elevation"
          sx={{ padding: "1rem", width: "100%", maxWidth: "1000px" }}
          raised
        >
          <Typography
            variant="h4"
            sx={{
              width: "100%",
              textAlign: "center",
              fontWeight: "600",
              marginBlock: "2rem 2rem",
            }}
          >
            TROŠKOVI
          </Typography>
          <ExpensesFilter
            expensesFilterTerm={expensesFilterTerm}
            setExpensesFilterTerm={setExpensesFilterTerm}
            suppliers={
              suppliers?.filter(
                (s) => expenses?.filter((e) => e.supplier === s.id).length! > 0
              ) ?? []
            }
          />
          <Grid item xs={12} sx={{ marginTop: "2rem" }}>
            <ExpensesTable
              suppliers={suppliers ?? []}
              expenses={filteredExpenses ?? []}
            />
          </Grid>
        </Card>
      </Grid>
    </Box>
  );
};
